// @ts-ignore - json-stringify-safe doesn't have types
import stringify from 'json-stringify-safe'

// Configuration
const isDev = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'dev'

// Log levels
const LOG_LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  http: 3,
  debug: 4,
}

const currentLogLevel = LOG_LEVELS[process.env.LOG_LEVEL as keyof typeof LOG_LEVELS] || LOG_LEVELS.info

// Format timestamp
function getTimestamp(): string {
  return new Date().toISOString()
}

// Parse error objects
interface AxiosLikeError extends Error {
  response?: {
    status?: number
    data?: unknown
  }
}

const parseError = (err: Error) => {
  const axiosError = err as AxiosLikeError
  return {
    errorMessage: err?.message,
    errorName: err?.name,
    errorStack: err?.stack,
    errorStatus: axiosError.response?.status,
    errorString: err ? stringify(err) : undefined,
    errorResponse: axiosError.response?.data ? stringify(axiosError.response?.data) : undefined,
  }
}

// Get metadata for structured logging (GCP compatible)
function getMeta(extras?: LogExtras, traceId?: string) {
  const meta: Record<string, unknown> = {
    timestamp: getTimestamp(),
  }

  // Add trace ID for GCP Log Explorer correlation
  if (traceId) {
    meta['logging.googleapis.com/trace'] = traceId
    meta.traceId = traceId // Keep both for compatibility
  }

  // Add labels for GCP
  if (extras?.labels) {
    meta.labels = extras.labels
  }

  // Add payload
  if (extras?.payload) {
    meta.payload = extras.payload
  }

  // Add error details
  if (extras?.error) {
    meta.error = parseError(extras.error)
  }

  return meta
}

export interface LogExtras {
  labels?: Record<string, string | number | boolean>
  payload?: unknown
  error?: Error
}

export interface IHttpRequest {
  cacheFillBytes?: string
  cacheHit?: boolean
  cacheLookup?: boolean
  cacheValidatedWithOriginServer?: boolean
  latency?: string
  protocol?: string
  referer?: string
  remoteIp?: string
  requestMethod?: string
  requestSize?: string
  requestUrl?: string
  responseSize?: string
  serverIp?: string
  status?: number
  userAgent?: string
}

export interface IHttpPayload {
  body: unknown
  headers: Record<string, string | string[]>
  params: Record<string, string>
  query: Record<string, string | string[]>
  response: unknown
  authorization: Record<string, string>
  size?: number
}

// Console output formatter for development
function formatConsoleOutput(level: string, message: string | unknown, meta: Record<string, unknown>) {
  if (!isDev) return null

  const levelLabel = level.toUpperCase().padEnd(5)
  const timestamp = new Date().toLocaleTimeString()
  const traceStr = meta.traceId ? ` [${meta.traceId}]` : ''

  let output = `${timestamp} ${levelLabel}${traceStr} ${message}`

  // Add relevant metadata
  if (meta.labels || meta.payload || meta.error) {
    const relevantMeta: Record<string, unknown> = {}
    if (meta.labels) relevantMeta.labels = meta.labels
    if (meta.payload) relevantMeta.payload = meta.payload
    if (meta.error) relevantMeta.error = meta.error
    output += '\n' + JSON.stringify(relevantMeta, null, 2)
  }

  return output
}

// GCP-compatible JSON formatter for production
function formatJsonOutput(level: string, message: string | unknown, meta: Record<string, unknown>) {
  // Map log levels to GCP severity levels
  const gcpSeverity: Record<string, string> = {
    error: 'ERROR',
    warn: 'WARNING',
    info: 'INFO',
    http: 'INFO',
    debug: 'DEBUG',
  }

  return JSON.stringify({
    severity: gcpSeverity[level] || 'INFO',
    message,
    ...meta,
  })
}

// Main logger output function
function logOutput(level: string, message: string | unknown, meta: Record<string, unknown>) {
  const levelValue = LOG_LEVELS[level as keyof typeof LOG_LEVELS]
  if (levelValue > currentLogLevel) return

  if (isDev) {
    const output = formatConsoleOutput(level, message, meta)
    if (output) {
      switch (level) {
        case 'error':
          console.error(output)
          break
        case 'warn':
          console.warn(output)
          break
        default:
          console.log(output)
          break
      }
    }
  } else {
    // Production: Always use structured JSON logging for GCP
    const output = formatJsonOutput(level, message, meta)
    console.log(output) // GCP expects all logs to stdout
  }
}

// Logger implementation
export const log = {
  log: (level: string, message: unknown, traceId?: string) => {
    let formattedMessage = message
    if (message instanceof Object && typeof message !== 'string') {
      formattedMessage = stringify(message)
    } else if (typeof message !== 'string') {
      formattedMessage = String(message)
    }
    logOutput(level, formattedMessage as string, getMeta(undefined, traceId))
  },

  error(message: unknown, extras?: LogExtras, traceId?: string) {
    if (message === undefined || message === null) {
      message = 'Unknown error (undefined message)'
    }

    let formattedMessage: string
    if (message instanceof Error) {
      formattedMessage = message.stack || message.message
      // Add error to extras if not already present
      if (!extras?.error) {
        extras = { ...extras, error: message }
      }
    } else {
      if (message instanceof Object && typeof message !== 'string') {
        formattedMessage = stringify(message)
      } else {
        formattedMessage = String(message)
      }
    }
    logOutput('error', formattedMessage, getMeta(extras, traceId))
  },

  http(httpRequest: IHttpRequest, httpPayload: IHttpPayload, latencySeconds: number, traceId?: string) {
    const message = `HTTP ${httpRequest.requestMethod} ${httpRequest.requestUrl} - ${httpRequest.status} (${latencySeconds}s)`
    const meta = getMeta(undefined, traceId)
    meta.httpRequest = httpRequest
    meta.httpPayload = httpPayload
    meta.latencySeconds = latencySeconds
    logOutput('http', message, meta as Record<string, unknown>)
  },

  warn(message: unknown, extras?: LogExtras, traceId?: string) {
    if (message === undefined || message === null) {
      message = 'Unknown warning (undefined message)'
    }

    let formattedMessage: string
    if (message instanceof Error) {
      formattedMessage = message.stack || message.message
    } else {
      if (message instanceof Object && typeof message !== 'string') {
        formattedMessage = stringify(message)
      } else {
        formattedMessage = String(message)
      }
    }
    logOutput('warn', formattedMessage, getMeta(extras, traceId))
  },

  info(message: unknown, extras?: LogExtras, traceId?: string) {
    if (message === undefined || message === null) {
      message = 'Unknown info (undefined message)'
    }

    let formattedMessage = message
    if (message instanceof Object && typeof message !== 'string') {
      formattedMessage = stringify(message)
    } else if (typeof message !== 'string') {
      formattedMessage = String(message)
    }
    logOutput('info', formattedMessage as string, getMeta(extras, traceId))
  },

  debug(message: unknown, extras?: LogExtras, traceId?: string) {
    let formattedMessage = message
    if (message instanceof Object && typeof message !== 'string') {
      formattedMessage = stringify(message)
    } else if (typeof message !== 'string') {
      formattedMessage = String(message)
    }
    logOutput('debug', formattedMessage as string, getMeta(extras, traceId))
  },
}

// Generate a unique trace ID for each request
export function generateTraceId(): string {
  // Format: timestamp-randomId for easy sorting in logs
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Create a logger instance for server-side use with event context
 * This should be used in server middleware and API routes
 * @param event - The H3 event from server middleware or API route
 */
export function createServerLogger(event: { context?: unknown }) {
  // Get trace ID from context (set by 00.logger middleware)
  // If not present, generate a new one and store it
  const context = event?.context as Record<string, unknown> | undefined
  let traceId = context?.traceId as string | undefined

  if (!traceId) {
    traceId = generateTraceId()
    if (context) {
      context.traceId = traceId
    }
  }

  return {
    traceId,
    error: (message: unknown, extras?: LogExtras) => log.error(message, extras, traceId),
    warn: (message: unknown, extras?: LogExtras) => log.warn(message, extras, traceId),
    info: (message: unknown, extras?: LogExtras) => log.info(message, extras, traceId),
    http: (httpRequest: IHttpRequest, httpPayload: IHttpPayload, latencySeconds: number) =>
      log.http(httpRequest, httpPayload, latencySeconds, traceId),
    debug: (message: unknown, extras?: LogExtras) => log.debug(message, extras, traceId),
  }
}
